import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, CircularProgress, Alert, Collapse, IconButton, Chip } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar, GridCellParams } from '@mui/x-data-grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useAuth } from '../context/AuthContext';
import { getStudentGrades, getStudentGradesSummary } from '../services/gradeService';
import { getStudentByUserId } from '../services/studentService';
import { getExams, getExam } from '../services/examService';
import { Grade, GradeSummary, Student } from '../types';
import { styled } from '@mui/material/styles';

interface GroupedGrades {
  exam_id: number | null;
  exam_title: string;
  grades: Grade[];
}

const ExpandIcon = styled(IconButton, {
  shouldForwardProp: (prop) => prop !== 'isexpanded',
})<{ isexpanded?: number }>(({ theme, isexpanded }) => ({
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
  transform: isexpanded ? 'rotate(90deg)' : 'rotate(0deg)',
}));

const MyGrades: React.FC = () => {
  const { user } = useAuth();
  const [grades, setGrades] = useState<Grade[]>([]);
  const [summary, setSummary] = useState<GradeSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [student, setStudent] = useState<Student | null>(null);
  const [examTitles, setExamTitles] = useState<{ [examId: string]: string }>({});
  const [expanded, setExpanded] = useState<string | null>(null); // Only one expanded at a time
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const fetchStudentAndGrades = async () => {
      if (!user?.id) return;
      setLoading(true);
      setError(null);
      try {
        const s = await getStudentByUserId(user.id);
        setStudent(s);
        const [gradesData, summaryData] = await Promise.all([
          getStudentGrades(s.id),
          getStudentGradesSummary(s.id),
        ]);
        setGrades(gradesData);
        setSummary(summaryData);
        // Fetch exam titles for all unique exam_ids
        const uniqueExamIds = Array.from(new Set(gradesData.map(g => g.exam_id).filter((id): id is number => id !== undefined)));
        if (uniqueExamIds.length > 0) {
          const examTitleMap: { [examId: string]: string } = {};
          await Promise.all(uniqueExamIds.map(async (examId) => {
            try {
              const exam = await getExam(examId);
              examTitleMap[examId.toString()] = exam.title;
            } catch {
              examTitleMap[examId.toString()] = `Exam #${examId}`;
            }
          }));
          setExamTitles(examTitleMap);
        }
      } catch (e: any) {
        setError(e?.response?.data?.detail || e.message || 'Failed to fetch grades');
      } finally {
        setLoading(false);
      }
    };
    fetchStudentAndGrades();
  }, [user]);

  // Group grades by exam_id
  const grouped: GroupedGrades[] = React.useMemo(() => {
    const map: { [examId: string]: GroupedGrades } = {};
    grades.forEach(g => {
      const key = g.exam_id || 'no_exam';
      if (!map[key]) {
        map[key] = {
          exam_id: g.exam_id || null,
          exam_title: g.exam_id ? (g.exam_id.toString()) : 'No Exam',
          grades: [],
        };
      }
      map[key].grades.push(g);
    });
    return Object.values(map);
  }, [grades]);

  // DataGrid columns
  const columns: GridColDef<Grade>[] = [
    { field: 'assignment_name', headerName: 'Assignment', flex: 1 },
    { field: 'grade', headerName: 'Grade', width: 100 },
    { field: 'max_grade', headerName: 'Max', width: 100 },
    { field: 'grade_type', headerName: 'Type', width: 120 },
    { field: 'course_title', headerName: 'Course', flex: 1 },
    { field: 'submitted_at', headerName: 'Submitted', width: 140, type: 'date',
      valueGetter: (params: any) => {
        // If params is a string (date), return as Date
        if (typeof params === 'string') return new Date(params);
        // If params is an object with value
        if (params && typeof params === 'object' && 'value' in params && params.value) {
          return new Date(params.value as string);
        }
        return null;
      },
      valueFormatter: (params: any) => {
        if (typeof params === 'string') return new Date(params).toLocaleDateString();
        if (params && typeof params === 'object' && 'value' in params && params.value) {
          return (new Date(params.value as string)).toLocaleDateString();
        }
        return '-';
      }
    },
  ];

  // Pagination logic
  const pagedGroups = grouped.slice(page * pageSize, page * pageSize + pageSize);
  const totalPages = Math.ceil(grouped.length / pageSize);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>My Grades</Typography>
      <Paper sx={{ p: 2, mb: 2 }}>
        {loading ? <CircularProgress /> : error ? (
          <Alert severity="error">{error}</Alert>
        ) : grades.length === 0 ? (
          <Typography color="text.secondary">No grades found.</Typography>
        ) : (
          <>
            {summary && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1">Average: {summary.average_percentage?.toFixed(1) || '-'}%</Typography>
                <Typography variant="subtitle2">Best: {summary.highest_score || '-'} | Worst: {summary.lowest_score || '-'}</Typography>
              </Box>
            )}
            {pagedGroups.map(group => {
              const examId = group.exam_id;
              const isExpanded = expanded === String(examId);
              const examTitle = examId ? examTitles[examId.toString()] : undefined;
              const examLoading = examId && !(examId.toString() in examTitles);
              // Find exam details if available
              let examDetails: any = null;
              if (examId && examTitles[examId.toString()]) {
                // examTitles only has title, but could be extended to store more info if needed
                // For now, just show title
                examDetails = { title: examTitles[examId.toString()] };
              }
              // Compute summary for this group
              const gradesArr = group.grades;
              const avg = gradesArr.length ? (gradesArr.reduce((a, g) => a + g.grade, 0) / gradesArr.length) : null;
              const best = gradesArr.length ? Math.max(...gradesArr.map(g => g.grade)) : null;
              const worst = gradesArr.length ? Math.min(...gradesArr.map(g => g.grade)) : null;
              return (
                <Box key={examId} sx={{ mb: 3, border: '1px solid #eee', borderRadius: 2, boxShadow: isExpanded ? 2 : 0, bgcolor: isExpanded ? '#f9f9fc' : '#fff' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, bgcolor: '#f5f5f5', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}>
                    <ExpandIcon size="small" isexpanded={isExpanded ? 1 : 0} onClick={() => setExpanded(isExpanded ? null : String(examId))}>
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </ExpandIcon>
                    <Box sx={{ ml: 1, flex: 1 }}>
                      <Typography variant="h6" sx={{ display: 'inline', mr: 1 }}>
                        {examId ? (examTitle || (examLoading ? <CircularProgress size={16} /> : `Exam #${examId}`)) : 'No Exam'}
                      </Typography>
                      {examId && examTitle && (
                        <>
                          {/* Optionally show more exam details here if available */}
                        </>
                      )}
                      {!examId && (
                        <Chip label="Not linked to exam" size="small" color="default" sx={{ ml: 1 }} />
                      )}
                    </Box>
                  </Box>
                  <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ p: 2, bgcolor: '#f9f9fc', borderBottomLeftRadius: 8, borderBottomRightRadius: 8 }}>
                      <DataGrid
                        autoHeight
                        rows={gradesArr.map((g, idx) => ({ ...g, id: g.id || idx }))}
                        columns={columns}
                        hideFooter
                        disableRowSelectionOnClick
                        slots={{ toolbar: GridToolbar }}
                      />
                      {/* Summary row for this exam group */}
                      <Box sx={{ mt: 2, display: 'flex', gap: 3, alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">Average: {avg !== null ? avg.toFixed(1) : '-'}</Typography>
                        <Typography variant="body2" color="text.secondary">Best: {best !== null ? best : '-'}</Typography>
                        <Typography variant="body2" color="text.secondary">Worst: {worst !== null ? worst : '-'}</Typography>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              );
            })}
            {/* Pagination controls */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <IconButton disabled={page === 0} onClick={() => setPage(p => Math.max(0, p - 1))}>
                <ExpandLessIcon style={{ transform: 'rotate(-90deg)' }} />
              </IconButton>
              <Typography sx={{ mx: 2 }}>Page {page + 1} of {totalPages}</Typography>
              <IconButton disabled={page >= totalPages - 1} onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}>
                <ExpandMoreIcon style={{ transform: 'rotate(-90deg)' }} />
              </IconButton>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default MyGrades; 